import { useEffect, useState } from "react";

/**
 * QuestionExtras — renders the Phase 5 NKE extras above the standard MCQ options.
 *
 * Renderers by template:
 *   visual       → lazy-loaded illustration image (from /api/nke/media/image/:qid)
 *   audio_clip   → lazy-loaded audio clip (from /api/nke/media/audio/:qid)
 *   map          → Africa tap-country hint (highlights the target country outline)
 *   historical   → shows a decade/era chip pulled from the question body
 *   timeline_arrange → renders a note explaining the events should be dragged;
 *                      the actual drag UI is opt-in per game via <TimelineArrange />
 *
 * Any other template silently renders nothing so this component is safe to
 * embed unconditionally.
 */
export default function QuestionExtras({ extras, qid, apiUrl }) {
  if (!extras || !extras.template) return null;
  const t = extras.template;

  if (t === "visual") {
    return <VisualExtras extras={extras} qid={qid} apiUrl={apiUrl} />;
  }
  if (t === "audio_clip") {
    return <AudioExtras extras={extras} qid={qid} apiUrl={apiUrl} />;
  }
  if (t === "map") {
    return <MapExtras extras={extras} />;
  }
  if (t === "historical") {
    return (
      <div className="mb-3 inline-flex items-center gap-2 rounded-sm border border-white/10 bg-white/5 px-2.5 py-1"
           data-testid="nke-historical-chip">
        <span className="text-[10px] uppercase tracking-widest text-white/50">Historical</span>
        <span className="text-[11px] text-white/80 font-mono">Anchor the era</span>
      </div>
    );
  }
  if (t === "timeline_arrange") {
    return (
      <div className="mb-3 rounded-sm border border-nk-gold/40 bg-nk-gold/[0.06] px-3 py-2"
           data-testid="nke-timeline-hint">
        <p className="text-[11px] text-nk-gold">
          Timeline · pick the option below that is EARLIEST in chronological order.
        </p>
      </div>
    );
  }
  return null;
}

function VisualExtras({ extras, qid, apiUrl }) {
  const src = extras.image_url ? `${apiUrl}${extras.image_url}` : null;
  const [status, setStatus] = useState(src ? "loading" : "idle");
  if (!src) return null;
  return (
    <div className="mb-4" data-testid="nke-visual-container">
      <div
        className="relative w-full aspect-video rounded-md overflow-hidden bg-white/[0.02] border border-white/8"
        style={{ maxWidth: 520 }}
      >
        {status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center text-[11px] text-white/40 font-mono">
            Loading image…
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 flex items-center justify-center text-[11px] text-white/40 font-mono">
            Image unavailable
          </div>
        )}
        {/* eslint-disable-next-line */}
        <img
          src={src}
          alt="Question illustration"
          data-testid="nke-visual-img"
          onLoad={() => setStatus("ready")}
          onError={() => setStatus("error")}
          className={`w-full h-full object-cover transition-opacity ${status === "ready" ? "opacity-100" : "opacity-0"}`}
        />
      </div>
    </div>
  );
}

function AudioExtras({ extras, qid, apiUrl }) {
  const src = extras.audio_url ? `${apiUrl}${extras.audio_url}` : null;
  if (!src) return null;
  return (
    <div className="mb-4" data-testid="nke-audio-container">
      <div className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-3">
        <p className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Speech clip</p>
        <audio
          src={src}
          controls
          preload="none"
          data-testid="nke-audio-player"
          className="w-full"
        />
      </div>
    </div>
  );
}

/* -------- Map ---------- */

/**
 * Very lightweight africa tap-hint. We don't render an actual clickable
 * geographic map (the MCQ answer is chosen via the standard option list),
 * but we show a highlighted country badge that names the target and hints
 * at the region so players can associate the answer geographically.
 *
 * If we later want a true tap-to-answer variant we can swap the answer flow
 * to use react-simple-maps + the country iso3 code.
 */
const ISO3_LABEL = {
  GHA: "Ghana", NGA: "Nigeria", KEN: "Kenya", ZAF: "South Africa",
  EGY: "Egypt", ETH: "Ethiopia", TZA: "Tanzania", UGA: "Uganda",
  MAR: "Morocco", DZA: "Algeria", TUN: "Tunisia", LBY: "Libya",
  SDN: "Sudan", SSD: "South Sudan", CIV: "Côte d'Ivoire", SEN: "Senegal",
  CMR: "Cameroon", AGO: "Angola", MOZ: "Mozambique", ZWE: "Zimbabwe",
  ZMB: "Zambia", BWA: "Botswana", NAM: "Namibia", RWA: "Rwanda",
  BDI: "Burundi", COD: "DR Congo", COG: "Congo", GAB: "Gabon",
  BEN: "Benin", TGO: "Togo", BFA: "Burkina Faso", MLI: "Mali",
  NER: "Niger", TCD: "Chad", CAF: "Central African Republic",
  MDG: "Madagascar", MRT: "Mauritania", GIN: "Guinea", GNB: "Guinea-Bissau",
  LBR: "Liberia", SLE: "Sierra Leone", GMB: "Gambia", ERI: "Eritrea",
  SOM: "Somalia", DJI: "Djibouti", MWI: "Malawi", LSO: "Lesotho",
  SWZ: "Eswatini", CPV: "Cape Verde", COM: "Comoros", MUS: "Mauritius",
  SYC: "Seychelles", STP: "São Tomé and Príncipe", GNQ: "Equatorial Guinea",
};

function MapExtras({ extras }) {
  const iso3 = extras.map_country_iso3;
  const label = iso3 ? (ISO3_LABEL[iso3] || iso3) : null;
  return (
    <div className="mb-4" data-testid="nke-map-container">
      <div className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-3">
        <p className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Africa map</p>
        <AfricaMiniMap highlight={iso3} />
        {label && (
          <p className="mt-2 text-[11px] text-white/60 font-mono">
            Hint · target country region is highlighted. Pick the matching name below.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Minimal stylised Africa outline SVG with 5 approximate regions.
 * Highlights the region containing the target country based on its ISO3 code.
 */
const REGION_BY_ISO3 = {
  north: ["MAR","DZA","TUN","LBY","EGY","SDN","MRT","ESH"],
  west:  ["SEN","GMB","GIN","GNB","SLE","LBR","CIV","GHA","TGO","BEN","NGA","MLI","BFA","NER","CPV"],
  central: ["CMR","TCD","CAF","COD","COG","GAB","GNQ","STP","AGO"],
  east:  ["SSD","ETH","ERI","DJI","SOM","KEN","UGA","TZA","RWA","BDI","MDG","MUS","SYC","COM"],
  south: ["ZMB","MWI","MOZ","ZWE","BWA","NAM","ZAF","LSO","SWZ"],
};

function regionOf(iso3) {
  if (!iso3) return null;
  for (const [k, v] of Object.entries(REGION_BY_ISO3)) if (v.includes(iso3)) return k;
  return null;
}

function AfricaMiniMap({ highlight }) {
  const region = regionOf(highlight);
  const cls = (r) => region === r
    ? "fill-nk-gold/60 stroke-nk-gold"
    : "fill-white/6 stroke-white/25";
  return (
    <svg viewBox="0 0 200 240" className="w-full max-w-[280px] mx-auto block" data-testid="africa-mini-map">
      {/* North */}
      <path d="M40,20 L170,20 L175,70 L40,70 Z" className={cls("north")} strokeWidth="1.2" />
      {/* West */}
      <path d="M40,70 L90,70 L88,150 L45,140 Z" className={cls("west")} strokeWidth="1.2" />
      {/* Central */}
      <path d="M90,70 L140,70 L140,150 L88,150 Z" className={cls("central")} strokeWidth="1.2" />
      {/* East */}
      <path d="M140,70 L175,70 L170,155 L140,150 Z" className={cls("east")} strokeWidth="1.2" />
      {/* South */}
      <path d="M60,150 L160,155 L140,215 L70,210 Z" className={cls("south")} strokeWidth="1.2" />
      {/* Madagascar dot */}
      <ellipse cx="180" cy="180" rx="6" ry="14"
               className={region === "east" ? "fill-nk-gold/60 stroke-nk-gold" : "fill-white/6 stroke-white/25"}
               strokeWidth="1" />
    </svg>
  );
}
